<template>
    <FormField
        :id="id"
        :label="label"
        :description="description"
        :help-text="helpText"
        :error="shownError"
        :required="required"
        :disabled="disabled"
    >
        <template v-if="$slots.label" #label><slot name="label"></slot></template>

        <template #default="{ fieldId, describedBy, invalid }">
            <PasswordInput
                :id="fieldId"
                v-model:val="valueState"
                @update:val="onFieldInput"
                @focusout="onFieldBlur"
                :placeholder="placeholder"
                :described-by="describedBy"
                :invalid="invalid"
                :required="required"
                :disabled="disabled"
                :read-only="readOnly"
                :maxlength="maxlength"
                :name="name"
                :auto-focus="autoFocus"
                :is-new-password="isNewPassword"
                :is-repeated-password="isRepeatedPassword"
                :use-inner-secure-conditions="useInnerSecureConditions"
            />
        </template>

        <template v-if="$slots.actions" #actions><slot name="actions"></slot></template>
    </FormField>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useVModel } from "@/utils/v-model";
import { FieldValidator, useFieldValidation } from "@/utils/form-validation";
import PasswordInput from "@/components/utils/PasswordInput.vue";
import FormField from "./FormField.vue";

export default defineComponent({
    components: {
        FormField,
        PasswordInput,
    },

    name: "PasswordField",

    // The control is PasswordInput, which takes its own props and has no place for loose
    // attributes. Leaving the fallthrough on means class and title land on the field instead
    // of being swallowed; anything the input itself needs has to be a prop of PasswordInput
    emits: ["update:modelValue", "update:error"],

    props: {
        modelValue: {
            type: String as PropType<string>,
            default: "",
        },
        label: String as PropType<string>,
        // Shown between the label and the control, forwarded to FormField
        description: String as PropType<string>,
        helpText: String as PropType<string>,
        error: String as PropType<string>,
        // Checks the value on blur, and again on every keystroke once a message is showing.
        // Bind the error with v-model:error to keep both sides in sync
        validator: Function as PropType<FieldValidator>,
        // PasswordInput falls back to its own generic text when this is empty
        placeholder: String as PropType<string>,
        // PasswordInput keeps its own limit when this is not given, and drops it with null
        maxlength: [Number, String] as PropType<number | string | null>,
        name: String as PropType<string>,
        required: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        disabled: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        // Shows a value that can be revealed and copied, but not edited
        readOnly: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        autoFocus: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        // Marks the field as a password being set, which enables the strength hints
        isNewPassword: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        // The confirmation field of a new password, which shows no strength of its own
        isRepeatedPassword: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        useInnerSecureConditions: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        id: String as PropType<string>,
    },

    setup(props) {
        // PasswordInput keeps its own v-model:val, so the difference is absorbed here
        const valueState = useVModel(props, "modelValue");

        return {
            valueState,
            ...useFieldValidation(props, () => valueState.value),
        };
    },
});
</script>
